/**
 * ProjectContext - Contexto para proyecto activo
 *
 * @version 2.0
 * @updated 2026-03-17
 * @module core/projects
 *
 * @description
 * Proporciona el ID del proyecto actualmente activo en la aplicación.
 * Usado para operaciones que requieren saber a qué proyecto pertenecen
 * (ejemplo: crear árboles, capas, escenarios).
 *
 * @remarks
 * **Versión 2.0** (actual):
 * - Carga automáticamente los proyectos disponibles del usuario
 * - Selecciona automáticamente el primer proyecto disponible (o el último usado)
 * - Maneja el caso de "sin proyectos disponibles"
 * - Guarda selección en localStorage
 *
 * **Cambios desde v1.0**:
 * - Eliminado hardcode de projectId por defecto
 * - Agregado estado de carga (isLoading)
 * - Agregado indicador de "sin proyectos" (hasProjects)
 */

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { projectsApi } from './api';
import { adminProjectsApi } from './admin-api';
import { useAuthStore } from '../auth/useAuthStore';
import type { Project } from './types';

const ACTIVE_PROJECT_KEY = 'wayline-active-project-id';
const LEGACY_ACTIVE_PROJECT_KEY = 'westeer-active-project-id';

function readActiveProjectId(): string | null {
  const current = localStorage.getItem(ACTIVE_PROJECT_KEY);
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_ACTIVE_PROJECT_KEY);
  if (legacy) {
    localStorage.setItem(ACTIVE_PROJECT_KEY, legacy);
    localStorage.removeItem(LEGACY_ACTIVE_PROJECT_KEY);
  }
  return legacy;
}

/**
 * Estado del contexto
 */
export interface ProjectContextState {
  /**
   * ID del proyecto actualmente activo
   */
  projectId: string | null;

  /**
   * Proyectos disponibles para el usuario
   */
  availableProjects: Project[];

  /**
   * Indica si está cargando los proyectos
   */
  isLoading: boolean;

  /**
   * Indica si el usuario tiene proyectos disponibles
   */
  hasProjects: boolean;

  /**
   * Cambiar proyecto activo
   */
  setProjectId: (id: string) => void;

  /**
   * Recargar proyectos disponibles
   */
  refetchProjects: () => Promise<void>;
}

/**
 * Contexto
 */
const ProjectContext = createContext<ProjectContextState | undefined>(undefined);

/**
 * Props del provider
 */
export interface ProjectProviderProps {
  children: ReactNode;
}

/**
 * Provider del contexto
 */
export function ProjectProvider({ children }: ProjectProviderProps): JSX.Element {
  const [projectId, setProjectIdState] = useState<string | null>(null);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Observar estado de autenticación
  const { isAuthenticated, token } = useAuthStore();

  /**
   * Carga los proyectos disponibles del usuario
   */
  const fetchProjects = useCallback(async (): Promise<void> => {
    // No cargar si no está autenticado
    if (!isAuthenticated || !token) {
      console.log('[ProjectContext] Usuario no autenticado, omitiendo carga de proyectos');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('[🔍 BOOTSTRAP] fetchProjects() iniciando:', {
        hasToken: !!token,
        tokenLength: token?.length ?? 0,
        isAuthenticated,
        userEmail: useAuthStore.getState().user?.email || 'null',
        timestamp: new Date().toISOString()
      });

      // Detectar si es SuperAdmin
      const authState = useAuthStore.getState();
      const user = authState.user;
      const isSuperAdmin = user?.role?.name === 'superadmin';

      console.log('[ProjectContext] Usuario:', {
        email: user?.email,
        role: user?.role?.name,
        isSuperAdmin,
      });

      // SuperAdmin usa /admin/projects, usuarios normales usan /projects
      const projects = isSuperAdmin
        ? await adminProjectsApi.list()
        : await projectsApi.list();

      setAvailableProjects(projects);

      console.log('[🔍 BOOTSTRAP] Proyectos disponibles cargados:', {
        count: projects.length,
        projects: projects.map(p => ({ id: p.id, name: p.name })),
        timestamp: new Date().toISOString()
      });

      // Auto-selección de proyecto
      if (projects.length > 0) {
        // Intentar restaurar el último proyecto usado desde localStorage
        const storedId = readActiveProjectId();
        console.log('[🔍 BOOTSTRAP] localStorage projectId:', {
          storedId,
          exists: !!storedId,
          timestamp: new Date().toISOString()
        });
        const storedProjectExists = projects.some((p: Project) => p.id === storedId);

        if (storedId && storedProjectExists) {
          // Restaurar proyecto guardado
          const restoredProject = projects.find(p => p.id === storedId);
          console.log('[🔍 BOOTSTRAP] Restaurando proyecto desde localStorage:', {
            projectId: storedId,
            projectName: restoredProject?.name,
            wasInList: storedProjectExists,
            timestamp: new Date().toISOString()
          });
          setProjectIdState(storedId);
        } else {
          // Seleccionar el primer proyecto disponible
          const firstProject = projects[0];
          console.log('[🔍 BOOTSTRAP] Seleccionando primer proyecto automáticamente:', {
            projectId: firstProject.id,
            projectName: firstProject.name,
            reason: storedId ? 'storedId no existe en lista' : 'no hay storedId',
            timestamp: new Date().toISOString()
          });
          setProjectIdState(firstProject.id);
          localStorage.setItem(ACTIVE_PROJECT_KEY, firstProject.id);
        }
      } else {
        // Sin proyectos disponibles
        setProjectIdState(null);
        localStorage.removeItem(ACTIVE_PROJECT_KEY);
        console.warn('[ProjectContext] ⚠️ El usuario no tiene proyectos disponibles');
      }
    } catch (error) {
      console.error('[ProjectContext] ❌ Error al cargar proyectos:', error);
      setAvailableProjects([]);
      setProjectIdState(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token]);

  /**
   * Actualiza el projectId y lo guarda en localStorage
   */
  const setProjectId = (id: string): void => {
    setProjectIdState(id);
    localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    console.log('[ProjectContext] Proyecto activo cambiado:', id);
  };

  /**
   * Cargar proyectos cuando cambia el estado de autenticación
   */
  useEffect(() => {
    if (isAuthenticated && token) {
      void fetchProjects();
    } else if (!token) {
      // Logout real: sin token → limpiar estado completo
      console.log('[🔍 BOOTSTRAP] Logout real, limpiando proyectos:', {
        isAuthenticated,
        hasToken: false,
        userEmail: useAuthStore.getState().user?.email || 'null',
        timestamp: new Date().toISOString()
      });
      setProjectIdState(null);
      setAvailableProjects([]);
      setIsLoading(false);
    } else {
      // token=true pero !isAuthenticated → auth resolviéndose (carga inicial)
      // NO limpiar proyectos, NO setIsLoading(false) — evita redirect prematuro a /no-projects
      console.log('[🔍 BOOTSTRAP] auth:resolving-with-token — projects:skip-clear-auth-resolving', {
        isAuthenticated,
        hasToken: !!token,
        timestamp: new Date().toISOString()
      });
    }
  }, [isAuthenticated, token, fetchProjects]);

  /**
   * Log cuando cambia el proyecto activo
   */
  useEffect(() => {
    if (projectId) {
      const project = availableProjects.find(p => p.id === projectId);
      console.log('[ProjectContext] Proyecto activo:', {
        id: projectId,
        name: project?.name || 'Desconocido',
      });
    }
  }, [projectId, availableProjects]);

  const value: ProjectContextState = {
    projectId,
    availableProjects,
    isLoading,
    hasProjects: availableProjects.length > 0,
    setProjectId,
    refetchProjects: fetchProjects,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

/**
 * Hook para usar el contexto
 */
export function useProject(): ProjectContextState {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
}
